import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Hybrid auth strategy (Safari / incognito friendly).
 *
 * We prefer httpOnly cookies (`kiddo_token`, set by the backend) — they're the
 * most secure storage and survive page reloads. But Safari ITP and most
 * browsers in incognito BLOCK third-party cookies, so any cross-site cookie
 * (frontend on kiddoplus.com → backend on api.kiddoplus.com) silently fails:
 * login looks fine, then every subsequent request comes back as 401.
 *
 * As a resilient fallback we ALSO mirror the JWT into localStorage and send
 * it as `Authorization: Bearer <token>` on every request. The backend's
 * `get_current_user` already checks the header first and falls back to the
 * cookie, so whichever channel survives the browser policy wins.
 *
 * Security note: localStorage is XSS-readable, so this is a *fallback only*
 * for environments where cookies are unavailable. We do not expose the token
 * anywhere else (no logging, no URL params, no React state).
 */
const TOKEN_KEY = "kiddo_auth_token";
const ADMIN_BACKUP_KEY = "kiddo_admin_backup_token";

export const tokenStore = {
  get: () => {
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
  },
  set: (token) => {
    try { if (token) localStorage.setItem(TOKEN_KEY, token); } catch { /* private mode */ }
  },
  clear: () => {
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
  },
  getAdminBackup: () => {
    try { return localStorage.getItem(ADMIN_BACKUP_KEY); } catch { return null; }
  },
  setAdminBackup: (token) => {
    try { if (token) localStorage.setItem(ADMIN_BACKUP_KEY, token); } catch { /* ignore */ }
  },
  clearAdminBackup: () => {
    try { localStorage.removeItem(ADMIN_BACKUP_KEY); } catch { /* ignore */ }
  },
};

// All requests carry the httpOnly cookie (when the browser allows it) AND the
// Bearer header (when localStorage has a token). Backend accepts either.
export const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((cfg) => {
  const t = tokenStore.get();
  if (t) {
    cfg.headers = cfg.headers || {};
    // Don't clobber a per-request Authorization (admin tools may set their own)
    if (!cfg.headers.Authorization && !cfg.headers.authorization) {
      cfg.headers.Authorization = `Bearer ${t}`;
    }
  }
  return cfg;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    // ── Global friendly error toasts ──────────────────────────────────────
    // Network error (request made but no response received — server down / offline)
    if (!err.response) {
      err._globalErrorHandled = true;
      toast.error("We're having trouble reaching the server. Please check your connection and try again.", {
        id: "kiddo-network-error",   // deduplicate rapid failures
        duration: 6000,
      });
    }
    // 5xx server-side error — replace raw technical details with a friendly message
    else if (err.response.status >= 500) {
      err._globalErrorHandled = true;
      toast.error("Something went wrong on our end. Please try again in a moment.", {
        id: "kiddo-server-error",
        duration: 6000,
      });
    }
    // 4xx errors (401 auth, 409 conflict, 422 validation, etc.) are intentional
    // business-logic responses handled per-call — do NOT toast here.
    return Promise.reject(err);
  }
);

export const auth = {
  signup: (data) => api.post("/auth/signup", data).then((r) => r.data),
  login: (data) => api.post("/auth/login", data).then((r) => r.data),
  google: (payload) => api.post("/auth/google", typeof payload === "string" ? { access_token: payload } : payload).then((r) => r.data),
  logout: (endpoint) => api.post("/auth/logout", endpoint ? { endpoint } : {}).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data),
  resetPassword: (token, new_password) => api.post("/auth/reset-password", { token, new_password }).then((r) => r.data),
  requestVerification: () => api.post("/auth/verify-email-request").then((r) => r.data),
  verifyEmail: (token) => api.post("/auth/verify-email", { token }).then((r) => r.data),
};

export const child = {
  get: () => api.get("/children/me").then((r) => r.data),
  save: (profile) => api.post("/children", profile).then((r) => r.data),
};

export const assessment = {
  questions: () => api.get("/assessment/questions").then((r) => r.data),
  eligibility: () => api.get("/assessment/eligibility").then((r) => r.data),
  // AI analyses can take 30-60s — give it room.
  submit: (answers) => api.post("/assessment/submit", { answers }, { timeout: 120000 }).then((r) => r.data),
  latest: () => api.get("/reports/latest").then((r) => r.data),
  list: () => api.get("/reports").then((r) => r.data),
  requestVerification: (rid) => api.post(`/reports/${rid}/verify-request`).then((r) => r.data),
  pdfUrl: (rid) => `${API}/reports/${rid}/pdf`,
};

export const deepDive = {
  eligibility: () => api.get("/deep-dive/eligibility").then((r) => r.data),
  questions: (domainKey) => api.get(`/deep-dive/questions/${domainKey}`).then((r) => r.data),
  // AI insights can take 20-40s for multi-domain submissions.
  submit: (answers) => api.post("/deep-dive/submit", { answers }, { timeout: 120000 }).then((r) => r.data),
  latest: () => api.get("/deep-dive/latest").then((r) => r.data),
  subSkill: (domainKey, subSkillKey) =>
    // First call generates the LLM insight (Gemini Flash, ~4-8s) — allow 30s.
    api.get(`/deep-dive/sub-skill/${encodeURIComponent(domainKey)}/${encodeURIComponent(subSkillKey)}`,
      { timeout: 30000 }).then((r) => r.data),
  domainInsight: (domainKey) =>
    api.get(`/deep-dive/domain-insight/${encodeURIComponent(domainKey)}`,
      { timeout: 30000 }).then((r) => r.data),
};

export const dailyTip = {
  today: () => api.get("/daily-tip").then((r) => r.data),
};

export const successStories = {
  list: () => api.get("/success-stories").then((r) => r.data),
  adminList: () => api.get("/success-stories/admin").then((r) => r.data),
  adminCreate: (data) => api.post("/success-stories/admin", data).then((r) => r.data),
  adminUpdate: (id, data) => api.patch(`/success-stories/admin/${id}`, data).then((r) => r.data),
  adminDelete: (id) => api.delete(`/success-stories/admin/${id}`).then((r) => r.data),
};

export const guidelines = {
  get: () => api.get("/guidelines").then((r) => r.data),
  save: (sections) => api.put("/admin/guidelines", { sections }).then((r) => r.data),
};

export const activities = {
  daily: () => {
    // Pass the user's local YYYY-MM-DD so daily rotation aligns with their midnight.
    const d = new Date();
    const local = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return api.get(`/activities?day=${local}`).then((r) => r.data);
  },
  library: () => api.get("/activities/library").then((r) => r.data),
  recent: () => api.get("/activities/recent").then((r) => r.data),
  detail: (id) => api.get(`/activities/${id}`).then((r) => r.data),
  complete: (id) => api.post("/activities/complete", { activity_id: id }).then((r) => r.data),
  weeklyProgress: () => api.get("/activities/weekly-progress").then((r) => r.data),
};

export const progress = {
  streak: () => api.get("/progress/streak").then((r) => r.data),
  compare: () => api.get("/progress/compare").then((r) => r.data),
  insights: (refresh = false) => api.get(`/progress/insights${refresh ? "?refresh=true" : ""}`).then((r) => r.data),
  calendar: () => api.get("/progress/calendar").then((r) => r.data),
};

export const courses = {
  list: () => api.get("/courses").then((r) => r.data),
  detail: (id) => api.get(`/courses/${id}`).then((r) => r.data),
  completeLesson: (cid, lid) => api.post(`/courses/${cid}/lesson/${lid}/complete`).then((r) => r.data),
  markWatched: (cid, lid) => api.post(`/courses/${cid}/lesson/${lid}/watched`).then((r) => r.data),
  listReviews: (cid) => api.get(`/courses/${cid}/reviews`).then((r) => r.data),
  submitReview: (cid, data) => api.post(`/courses/${cid}/reviews`, data).then((r) => r.data),
};

export const feedback = {
  myRating: () => api.get("/feedback/my").then((r) => r.data),
  submit: (data) => api.post("/feedback", data).then((r) => r.data),
};

export const payments = {
  createOrder: (plan, courseId) => api.post(`/payments/create-order?plan=${plan}${courseId ? `&course_id=${encodeURIComponent(courseId)}` : ""}`).then((r) => r.data),
  verify: (data) => api.post("/payments/verify", data).then((r) => r.data),
  history: () => api.get("/payments/history").then((r) => r.data),
  cancelPlan: () => api.post("/payments/cancel-plan").then((r) => r.data),
  methods: () => api.get("/payments/methods").then((r) => r.data),
  // Razorpay subscriptions
  createSubscription: (plan) => api.post("/payments/create-subscription", { plan }).then((r) => r.data),
  verifySubscription: (data) => api.post("/payments/verify-subscription", data).then((r) => r.data),
  subscription: () => api.get("/payments/subscription").then((r) => r.data),
  cancelSubscription: () => api.post("/payments/cancel-subscription").then((r) => r.data),
};

export const settings = {
  updateProfile: (data) => api.patch("/auth/profile", data).then((r) => r.data),
  changeEmail: (data) => api.post("/auth/change-email", data).then((r) => r.data),
  changePassword: (data) => api.post("/auth/change-password", data).then((r) => r.data),
  getNotifications: () => api.get("/users/notifications").then((r) => r.data),
  updateNotifications: (prefs) => api.patch("/users/notifications", prefs).then((r) => r.data),
  deleteChild: (data) => api.delete("/children/me", { data }).then((r) => r.data),
  deleteAccount: (data) => api.delete("/users/me", { data }).then((r) => r.data),
  contactSupport: (data) => api.post("/support/contact", data).then((r) => r.data),
  reportBug: (data) => api.post("/support/bug", data).then((r) => r.data),
};

export const waitlist = {
  join: (data) => api.post("/waitlist", data).then((r) => r.data),
};

// Public book-review submissions — readers landing on /book share their thoughts.
export const bookReviews = {
  submit: (data) => api.post("/book-reviews", data).then((r) => r.data),
  featured: (limit = 50) => api.get(`/book-reviews/featured?limit=${limit}`).then((r) => r.data),
};

// Bookmarked activities — pure wishlist, no streak coupling.
export const saved = {
  list: () => api.get("/users/saved-activities").then((r) => r.data),
  add: (activity_id) => api.post("/users/saved-activities", { activity_id }).then((r) => r.data),
  remove: (activity_id) => api.delete(`/users/saved-activities/${activity_id}`).then((r) => r.data),
};

export const notifications = {
  inbox: () => api.get("/notifications/inbox").then((r) => r.data),
  markRead: (id) => api.post(`/notifications/${id}/read`).then((r) => r.data),
  dismiss: (id) => api.post(`/notifications/${id}/dismiss`).then((r) => r.data),
};

export const disa = {
  conversations: () => api.get("/disa/conversations").then((r) => r.data),
  newConversation: () => api.post("/disa/conversations").then((r) => r.data),
  messages: (cid) => api.get(`/disa/conversations/${cid}/messages`).then((r) => r.data),
  archive: (cid) => api.delete(`/disa/conversations/${cid}`).then((r) => r.data),
  refreshKb: () => api.post("/disa/kb/refresh").then((r) => r.data),
};

export const admin = {
  users: () => api.get("/admin/users").then((r) => r.data),
  userDetail: (id) => api.get(`/admin/users/${id}`).then((r) => r.data),
  updateUserPlan: (id, data) => api.patch(`/admin/users/${id}/plan`, data).then((r) => r.data),
  updateUserRole: (id, role) => api.patch(`/admin/users/${id}/role`, { role }).then((r) => r.data),
  deleteUser: (id, confirm_email) => api.delete(`/admin/users/${id}`, { data: { confirm_email } }).then((r) => r.data),
  waitlist: () => api.get("/admin/waitlist").then((r) => r.data),
  waitlistStatus: (id, status) => api.post(`/admin/waitlist/${id}/status`, { status }).then((r) => r.data),
  waitlistDelete: (id) => api.delete(`/admin/waitlist/${id}`).then((r) => r.data),
  impersonate: (uid) => api.post(`/admin/impersonate/${uid}`).then((r) => r.data),
  stopImpersonate: () => api.post("/admin/stop-impersonate").then((r) => r.data),
  feedback: () => api.get("/admin/feedback").then((r) => r.data),
  therapistQueue: () => api.get("/admin/therapist-queue").then((r) => r.data),
  activities: () => api.get("/admin/activities").then((r) => r.data),
  createActivity: (data) => api.post("/admin/activities", data).then((r) => r.data),
  updateActivity: (id, data) => api.put(`/admin/activities/${id}`, data).then((r) => r.data),
  deleteActivity: (id) => api.delete(`/admin/activities/${id}`).then((r) => r.data),
  aiActivity: (data) => api.post("/admin/activities/ai-generate", data).then((r) => r.data),
  aiAutofill: (data) => api.post("/admin/ai-autofill", data).then((r) => r.data),
  verifyQueue: (qid, data) => api.post(`/admin/therapist-queue/${qid}/verify`, data).then((r) => r.data),
  questions: () => api.get("/admin/questions").then((r) => r.data),
  addQuestion: (data) => api.post("/admin/questions", data).then((r) => r.data),
  updateQuestion: (id, data) => api.put(`/admin/questions/${id}`, data).then((r) => r.data),
  deleteQuestion: (id) => api.delete(`/admin/questions/${id}`).then((r) => r.data),
  courses: () => api.get("/admin/courses").then((r) => r.data),
  createCourse: (data) => api.post("/admin/courses", data).then((r) => r.data),
  updateCourse: (id, data) => api.put(`/admin/courses/${id}`, data).then((r) => r.data),
  deleteCourse: (id) => api.delete(`/admin/courses/${id}`).then((r) => r.data),
  patchCourseMetrics: (id, data) => api.patch(`/admin/courses/${id}/metrics`, data).then((r) => r.data),
  reviews: () => api.get("/admin/reviews").then((r) => r.data),
  setReviewStatus: (rid, status) => api.patch(`/admin/reviews/${rid}`, { status }).then((r) => r.data),
  deleteReview: (rid) => api.delete(`/admin/reviews/${rid}`).then((r) => r.data),
  // Resources Hub CRUD
  resources: () => api.get("/admin/resources").then((r) => r.data),
  createResource: (data) => api.post("/admin/resources", data).then((r) => r.data),
  updateResource: (id, data) => api.patch(`/admin/resources/${id}`, data).then((r) => r.data),
  deleteResource: (id) => api.delete(`/admin/resources/${id}`).then((r) => r.data),
  // Deep-Dive question editor
  deepDiveQuestions: () => api.get("/deep-dive/admin/questions").then((r) => r.data),
  updateDeepDiveQuestion: (id, payload) => api.patch(`/deep-dive/admin/questions/${id}`, typeof payload === "string" ? { text: payload } : payload).then((r) => r.data),
  renameDeepDiveSubSkill: (data) => api.patch("/deep-dive/admin/sub-skill", data).then((r) => r.data),
  addDeepDiveQuestion: (data) => api.post("/deep-dive/admin/questions", data).then((r) => r.data),
  deleteDeepDiveQuestion: (id) => api.delete(`/deep-dive/admin/questions/${id}`).then((r) => r.data),
  reseedDeepDive: () => api.post("/deep-dive/admin/reseed").then((r) => r.data),
  // Domain / sub-skill illustration uploads
  domainMeta: () => api.get("/admin/domains/meta").then((r) => r.data),
  uploadDomainImage: (domainKey, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/admin/domains/${domainKey}/image`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  clearDomainImage: (domainKey) => api.delete(`/admin/domains/${domainKey}/image`).then((r) => r.data),
  subSkillMeta: () => api.get("/deep-dive/admin/sub-skills/meta").then((r) => r.data),
  uploadSubSkillImage: (domainKey, subSkillKey, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/deep-dive/admin/sub-skills/${domainKey}/${subSkillKey}/image`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  clearSubSkillImage: (domainKey, subSkillKey) =>
    api.delete(`/deep-dive/admin/sub-skills/${domainKey}/${subSkillKey}/image`).then((r) => r.data),
  // Book reviews (read-only + CSV export of leads from /book page)
  bookReviews: () => api.get("/admin/book-reviews").then((r) => r.data),
  bookReviewUpdate: (rid, payload) => api.patch(`/admin/book-reviews/${rid}`, payload).then((r) => r.data),
  bookReviewDelete: (rid) => api.delete(`/admin/book-reviews/${rid}`).then((r) => r.data),
  setBookReviewFeatured: (rid, featured) => api.patch(`/admin/book-reviews/${rid}`, { featured }).then((r) => r.data),
  bookReviewsExportUrl: () => `${api.defaults.baseURL}/admin/book-reviews/export.csv`,
  updateBookReview: (id, data) => api.patch(`/admin/book-reviews/${id}`, data).then((r) => r.data),
  deleteBookReview: (id) => api.delete(`/admin/book-reviews/${id}`).then((r) => r.data),
  // Free Screening leads (read + create + status change + delete from admin)
  fsLeads: (limit = 200) => api.get(`/free-screening/leads?limit=${limit}`).then((r) => r.data),
  fsCreate: (payload) => api.post("/free-screening/leads/admin", payload).then((r) => r.data),
  fsUpdateStatus: (id, status) => api.patch(`/free-screening/leads/${id}/status`, { status }).then((r) => r.data),
  fsUpdateNotes: (id, admin_notes) => api.patch(`/free-screening/leads/${id}/notes`, { admin_notes }).then((r) => r.data),
  fsDelete: (id) => api.delete(`/free-screening/leads/${id}`).then((r) => r.data),
  // Observability mini-dashboard
  systemHealth: () => api.get("/admin/system/health").then((r) => r.data),
  // Admin push notifications management
  notifAudienceCounts: () => api.get("/admin/notifications/audience-counts").then((r) => r.data),
  notifSend: (data) => api.post("/admin/notifications/send", data).then((r) => r.data),
  notifSchedule: (data) => api.post("/admin/notifications/schedule", data).then((r) => r.data),
  notifHistory: (limit = 50) => api.get(`/admin/notifications/history?limit=${limit}`).then((r) => r.data),
  notifAnalytics: () => api.get("/admin/notifications/analytics").then((r) => r.data),
  notifResend: (id) => api.post(`/admin/notifications/${id}/resend`).then((r) => r.data),
  notifCancel: (id) => api.post(`/admin/notifications/${id}/cancel`).then((r) => r.data),
};

// Public Resources + Explore Activities (no auth required) ----------------
export const publicApi = {
  resources: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== "" && v !== "all"),
    ).toString();
    return api.get(`/resources${qs ? `?${qs}` : ""}`).then((r) => r.data);
  },
  // Public activity browsing — backend is open, library already returns all
  exploreActivities: () => api.get("/explore-activities").then((r) => r.data),
};
